/**
 * Structure revisions — a frozen, whole-tree state of one hierarchy kind in a
 * workspace (v<major>.<minor>), and the diff between two of them.
 *
 * Spec: docs/9-exploration/PRD-structure-versioning-and-transitions.md (FR-1,
 * FR-6). Tables: `structure_revisions` / `structure_revision_nodes` (mig 730).
 *
 * The live tables stay the current projection; this module only reads history.
 * The diff is by node identity: a node that keeps its id "continues", whatever
 * happened to its name or parent. Merges and splits are Phase 3 dispositions —
 * until then a node absent from the later revision reads as retired.
 */

import type { HierarchyKind } from './hierarchyTypes';

/** DOM id of a kind's history panel — the reorg workspace links here after authorizing. */
export function structureHistoryAnchorId(kind: HierarchyKind): string {
  return `structure-history-${kind}`;
}

export type StructureRevisionOrigin = 'baseline' | 'edit' | 'reorg';
export type StructureRevisionStatus = 'draft' | 'authorized' | 'superseded';

export interface StructureRevision {
  id: string;
  workspaceId: string;
  kind: HierarchyKind;
  major: number;
  minor: number;
  origin: StructureRevisionOrigin;
  status: StructureRevisionStatus;
  label: string | null;
  rationale: string | null;
  nodeCount: number;
  effectiveAt: string;
  authorizedBy: string | null;
  supersedesRevisionId: string | null;
}

export interface StructureRevisionNode {
  revisionId: string;
  nodeId: string;
  parentNodeId: string | null;
  name: string;
  level: string | null;
  depth: number;
  sortOrder: number;
  pathNames: string[];
}

export type StructureNodeChange = 'added' | 'renamed' | 'moved' | 'renamed_and_moved' | 'retired';

export interface StructureNodeDiff {
  nodeId: string;
  change: StructureNodeChange;
  /** Name in the later revision (or the earlier one, for a retired node). */
  name: string;
  previousName?: string;
  /** Root-first path in the later revision (earlier, for a retired node). */
  pathNames: string[];
  previousPathNames?: string[];
}

export function formatRevisionVersion(rev: Pick<StructureRevision, 'major' | 'minor'>): string {
  return `v${rev.major}.${rev.minor}`;
}

/** Newest first: major desc, then minor desc. */
export function sortRevisionsNewestFirst<T extends Pick<StructureRevision, 'major' | 'minor'>>(revs: readonly T[]): T[] {
  return [...revs].sort((a, b) => b.major - a.major || b.minor - a.minor);
}

const CHANGE_ORDER: Record<StructureNodeChange, number> = {
  added: 0,
  renamed: 1,
  moved: 2,
  renamed_and_moved: 3,
  retired: 4,
};

/**
 * What changed from `previous` to `next`, by node identity. A node whose own
 * name and parent are unchanged is not reported even if an ancestor was
 * renamed or moved — the ancestor's entry already says so.
 */
export function diffStructureRevisions(
  previous: readonly StructureRevisionNode[],
  next: readonly StructureRevisionNode[],
): StructureNodeDiff[] {
  const before = new Map(previous.map((n) => [n.nodeId, n]));
  const after = new Map(next.map((n) => [n.nodeId, n]));
  const out: StructureNodeDiff[] = [];

  for (const n of next) {
    const p = before.get(n.nodeId);
    if (!p) {
      out.push({ nodeId: n.nodeId, change: 'added', name: n.name, pathNames: n.pathNames });
      continue;
    }
    const renamed = p.name !== n.name;
    const moved = p.parentNodeId !== n.parentNodeId;
    if (!renamed && !moved) continue;
    out.push({
      nodeId: n.nodeId,
      change: renamed && moved ? 'renamed_and_moved' : renamed ? 'renamed' : 'moved',
      name: n.name,
      previousName: renamed ? p.name : undefined,
      pathNames: n.pathNames,
      previousPathNames: p.pathNames,
    });
  }

  for (const p of previous) {
    if (!after.has(p.nodeId)) {
      out.push({ nodeId: p.nodeId, change: 'retired', name: p.name, pathNames: p.pathNames });
    }
  }

  return out.sort(
    (a, b) => CHANGE_ORDER[a.change] - CHANGE_ORDER[b.change] || a.pathNames.join('/').localeCompare(b.pathNames.join('/')),
  );
}

/** Depth-first, parents before children, siblings by sort order then name. */
export function orderRevisionNodesAsTree(nodes: readonly StructureRevisionNode[]): StructureRevisionNode[] {
  const ids = new Set(nodes.map((n) => n.nodeId));
  const byParent = new Map<string | null, StructureRevisionNode[]>();
  for (const n of nodes) {
    const key = n.parentNodeId && ids.has(n.parentNodeId) ? n.parentNodeId : null;
    const list = byParent.get(key) ?? [];
    list.push(n);
    byParent.set(key, list);
  }
  const out: StructureRevisionNode[] = [];
  const seen = new Set<string>();
  const walk = (parent: string | null) => {
    const kids = (byParent.get(parent) ?? []).sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
    for (const k of kids) {
      if (seen.has(k.nodeId)) continue;
      seen.add(k.nodeId);
      out.push(k);
      walk(k.nodeId);
    }
  };
  walk(null);
  return out;
}
